import { InfoKeyValue } from '@/components/info-key-value';
import { Button } from '@/components/ui/button';
import { DialogHeader, Dialog, DialogTrigger, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { getCoachVendorForAdmin } from '@/lib/api-calls';
import {
  CoachPaymentDepositInfoStatus,
  getCoachACHPaymentOrNull,
  getCoachPaymentInternationalOrNull,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { DepositInfoStatus } from './deposit-info-status';

type PaymentInstrumentModalProps = {
  paymentInstrumentId: string | null;
  depositInfoStatus: CoachPaymentDepositInfoStatus;
};

export const PaymentInstrumentModal = (props: PaymentInstrumentModalProps) => {
  const { paymentInstrumentId, depositInfoStatus } = props;
  const [isOpen, setIsOpen] = useState(false);

  const { data: vendor } = useQuery({
    ...getCoachVendorForAdmin(paymentInstrumentId ?? undefined),
    enabled: !!paymentInstrumentId && isOpen,
  });

  if (!paymentInstrumentId) return null;

  const achVendor = getCoachACHPaymentOrNull(vendor);
  const internationalVendor = getCoachPaymentInternationalOrNull(vendor);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-800 no-underline">
          {paymentInstrumentId}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Brex - Bank information</DialogTitle>
          {achVendor && (
            <div className="grid grid-cols-2 pt-4 gap-4">
              <InfoKeyValue label="Account Holder Name" value={achVendor?.company_name} />
              <InfoKeyValue label="Account Type" value={achVendor?.payment_accounts?.details.account_type} />
              <InfoKeyValue label="Routing Number" value={achVendor?.payment_accounts?.details.routing_number} />
              <InfoKeyValue label="Account Number" value={achVendor?.payment_accounts?.details.account_number} />
              <InfoKeyValue label="Status" value={<DepositInfoStatus depositInfoStatus={depositInfoStatus} />} />
            </div>
          )}
          {internationalVendor && (
            <div className="grid grid-cols-2 pt-4 gap-4">
              <InfoKeyValue label="Account Holder Name" value={internationalVendor.company_name} />
              <InfoKeyValue label="Swift / BIC code" value={internationalVendor.payment_accounts?.details.swift_code} />
              <InfoKeyValue label="IBAN / Account number" value={internationalVendor.payment_accounts?.details.iban} />
              <InfoKeyValue label="Country" value={internationalVendor.payment_accounts?.details.address.country} />
              <InfoKeyValue label="City" value={internationalVendor.payment_accounts?.details.address.city} />
              <InfoKeyValue
                label="Address"
                value={
                  (internationalVendor?.payment_accounts.details.address.line1 ?? '') +
                  ' ' +
                  (internationalVendor?.payment_accounts.details.address.line2 ?? '')
                }
              />
              <InfoKeyValue label="Status" value={<DepositInfoStatus depositInfoStatus={depositInfoStatus} />} />
              <InfoKeyValue
                label="Zip code"
                value={internationalVendor.payment_accounts?.details.address.postal_code}
              />
            </div>
          )}
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
